import { Result } from "../../../shared/api/services/result";
import { requestMonitor } from "../../../shared/api/services/RequestMonitor";
import { triggerDoctorInfoRefresh } from "../../../shared/utils/events/DoctorInfoRefreshEvent";
import { triggerDoctorSessionExpired } from "../../../shared/utils/events/DoctorLogoutEvent";
import { isMockEnabled, getMockMyInfo } from "../../../shared/dev/mockData";

import {
  ACTIVE_ACCOUNT_KEY,
  UI_PREFS_APPLIED_EVENT,
  activateAccountPrefs,
  doctorIdFromToken,
  isPreservedOnLogout,
  migrateUiPrefs,
} from "../../../shared/storage/uiPreferences";

// localStorage keys that survive clearTokens() are the UI preferences only (design + language:
// the device default and each doctor's own record) — see src/shared/storage/uiPreferences.ts.
// Everything else (tokens, doctor data, drafts, request ids) is session data.
/**
 * Error details for a Result: the server's `error.details` plus its `message_code` and the HTTP status,
 * so screens can tell a rate limit (429 otp.rate_limited, details.retry_after) or an existing account
 * (409 qr.identity.exists) apart without parsing the localized sentence.
 */
function errorDetailsOf(body: any, status: number): Record<string, any> {
  const details = body?.error?.details;
  const base = details && typeof details === "object" && !Array.isArray(details) ? details : {};
  return { ...base, message_code: body?.error?.message_code ?? base.message_code, status };
}

const TOKEN_KEYS = ["access_token", "refresh_token", "token_expiry"];

function localStorageKeys(): string[] {
  const s: any = localStorage;
  if (typeof s.length === "number" && typeof s.key === "function") {
    const keys: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k !== null && k !== undefined) keys.push(k);
    }
    return keys;
  }
  return Object.keys(s);
}

function sessionStorageOrNull(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

// Cross-tab lock name for token refresh (Web Locks API, where available).
const REFRESH_LOCK_NAME = "rx360-doctor-token-refresh";
// Statuses with which the refresh endpoint rejects the refresh token itself.
const REFRESH_REJECTED_STATUSES = [400, 401, 403, 422, 444];

// =====================================
// AUTH TYPES
// =====================================

export interface OTPResponse {
  message: string;
  otp_code: string;
}

export interface AuthTokens {
  token: string;
  token_type: string;
  expires_in: number;
}

export interface ConfirmOTPResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  doctor: any;
  clinic: any | null;
}

export interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

export interface LogoutResponse {
  status: boolean;
  status_code: number;
  data: {
    message: string;
  };
  message: string | null;
}

export interface GetPhoneByLicenseResponse {
  // "otp": proceed with phone confirmation + OTP (phone_last_4_digits set).
  // "qr_required": doctor not found or license expired → scan the IMA ID QR.
  action: "otp" | "qr_required";
  phone_last_4_digits: string | null;
  reason?: "not_found" | "expired" | null;
}

export interface QrPrefill {
  reg_num: number;
  full_name_ar?: string | null;
  full_name_en?: string | null;
  mem_prof?: string | null;
  mem_end_date?: string | null;
}

export interface QrVerifyResponse {
  matched: boolean;
  // "otp": existing doctor refreshed (use reg_num for the license OTP flow).
  // "qr_verified_new": new doctor verified → use qr_pending_token for phone+OTP.
  // "client_or_manual_qr_fallback": automation failed → manual entry (qr_url set).
  action: "otp" | "qr_verified_new" | "client_or_manual_qr_fallback";
  reg_num?: number | null;
  phone_last_4_digits?: string | null;
  qr_pending_token?: string | null;
  prefill?: QrPrefill | null;
  reason?: string | null;
  qr_url?: string | null;
}

export interface QrManualSubmitPayload {
  uid: string;
  reg_num: number;
  full_name_ar?: string;
  full_name_en?: string;
  mem_prof?: string;
  reg_date?: string;
  mem_start_date?: string;
  mem_end_date?: string;
  entered_license?: number;
}

export interface QrManualSubmitResponse {
  matched: boolean;
  // "qr_manual_pending": proceed to phone+OTP with qr_pending_token.
  // "pending_review_no_login": established account → queued for staff review, no login.
  action: "qr_manual_pending" | "pending_review_no_login";
  qr_pending_token?: string | null;
  prefill?: QrPrefill | null;
  requires_staff_review: boolean;
  message?: string | null;
}

export interface DoctorVerificationResponse {
  name_ar: string;
  name_en: string;
  license_number: string;
  registration_number: string;
  registration_date: string;
  print_date: string;
  renewal_date: string;
  expiry_date: string;
  photo_url: string;
  photo_thumbnail_url: string;
  photo_base64: string;
  verification_url: string;
  doctor_id: string;
  scraped_at: string;
  status: string;
  data_quality: string;
  verification_successful: boolean;
  scraping_method: string;
  response_time_ms: number;
  cache_hit: boolean;
  name_match_confidence: number;
  license_format_valid: boolean;
  dates_consistent: boolean;
}

// =====================================
// AUTH API SERVICE
// =====================================

/**
 * AuthApiService handles all authentication-related API calls
 * - OTP sending and verification
 * - Token management (refresh, logout)
 * - License-based authentication
 */
export class AuthApiService {
  private static instance: AuthApiService;

  static getInstance(): AuthApiService {
    if (!AuthApiService.instance) {
      AuthApiService.instance = new AuthApiService();
    }
    return AuthApiService.instance;
  }

  private baseURL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  // Request deduplication
  private requestCache = new Map<string, Promise<Result<any>>>();
  private refreshInFlight: Promise<Result<RefreshTokenResponse>> | null = null;

  // Request tracking for debugging
  private requestCounter = new Map<string, number>();

  constructor() {
    this.loadStoredTokens();
    try {
      migrateUiPrefs(localStorage);
    } catch {
      /* storage unavailable */
    }
    // Keep this tab's copy in step when another tab refreshes (rotates) or clears the tokens.
    if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
      window.addEventListener("storage", (event: StorageEvent) => {
        if (event.key === null || event.key === "access_token" || event.key === "refresh_token") {
          this.loadStoredTokens();
        }
      });
    }
  }

  // =====================================
  // TOKEN MANAGEMENT
  // =====================================

  loadStoredTokens() {
    this.accessToken = localStorage.getItem("access_token");
    this.refreshToken = localStorage.getItem("refresh_token");
  }

  setTokens(accessToken: string, refreshToken: string, expiresIn?: number) {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem("access_token", accessToken);
    localStorage.setItem("refresh_token", refreshToken);
    if (expiresIn) {
      const expiryTime = Date.now() + expiresIn * 1000;
      localStorage.setItem("token_expiry", String(expiryTime));
    }
    this.startAccountSession(accessToken);
  }

  /**
   * On a sign-in (a token for a doctor other than the current session's): drop the previous
   * doctor's session data if they never logged out, then apply this doctor's own UI preferences.
   * A token refresh for the same doctor changes nothing.
   */
  private startAccountSession(accessToken: string) {
    try {
      const doctorId = doctorIdFromToken(accessToken);
      if (!doctorId) return;
      const previous = localStorage.getItem(ACTIVE_ACCOUNT_KEY);
      if (previous === doctorId) return;
      if (previous) this.purgeSessionData(TOKEN_KEYS);
      const outcome = activateAccountPrefs(doctorId, localStorage, sessionStorageOrNull());
      if (!outcome.unchanged && typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(UI_PREFS_APPLIED_EVENT, { detail: outcome.prefs }));
      }
    } catch {
      /* storage unavailable — preferences simply stay as they are */
    }
  }

  /** Removes every localStorage key except the UI preferences and `keep`. */
  private purgeSessionData(keep: string[] = []) {
    localStorageKeys()
      .filter((key) => !isPreservedOnLogout(key) && !keep.includes(key))
      .forEach((key) => localStorage.removeItem(key));
  }

  clearTokens() {
    console.log("🧹 [AuthApiService] Clearing all tokens and localStorage data...");

    this.accessToken = null;
    this.refreshToken = null;

    // UI preferences are not session data: keep the device default and each doctor's own
    // design/language record across logout and token-refresh failures. Everything else —
    // tokens, doctor data, drafts, request ids — is removed.
    const preserved = localStorageKeys()
      .filter(isPreservedOnLogout)
      .map((key) => [key, localStorage.getItem(key)] as const);
    localStorage.clear();
    preserved.forEach(([key, value]) => {
      if (value !== null) localStorage.setItem(key, value);
    });

    console.log("✅ [AuthApiService] All localStorage data cleared successfully");
  }

  getTokenExpiry(): number | null {
    const expiry = localStorage.getItem("token_expiry");
    return expiry ? Number(expiry) : null;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  getRefreshToken(): string | null {
    return this.refreshToken;
  }

  isTokenValid(): boolean {
    if (!this.accessToken) return false;
    const expiry = localStorage.getItem("token_expiry");
    if (!expiry) return true;
    // Add 60s buffer to trigger refresh before actual expiry
    return Date.now() < Number(expiry) - 60000;
  }

  // =====================================
  // REQUEST DEDUPLICATION
  // =====================================

  private deduplicate<T>(
    key: string,
    request: () => Promise<Result<T>>
  ): Promise<Result<T>> {
    requestMonitor.startRequest(key);
    const startTime = Date.now();

    const count = this.requestCounter.get(key) || 0;
    this.requestCounter.set(key, count + 1);

    if (this.requestCache.has(key)) {
      return this.requestCache.get(key)!;
    }

    const promise = request()
      .then((result) => {
        requestMonitor.endRequest(
          key,
          undefined,
          result.isSuccess,
          Date.now() - startTime,
          result.error
        );
        return result;
      })
      .catch((error) => {
        requestMonitor.endRequest(
          key,
          undefined,
          false,
          Date.now() - startTime,
          error.message
        );
        throw error;
      })
      .finally(() => {
        this.requestCache.delete(key);
        this.requestCounter.set(key, 0);
      });

    this.requestCache.set(key, promise);
    return promise;
  }

  private getCacheKey(method: string, params?: any): string {
    if (params) {
      return `${method}_${JSON.stringify(params)}`;
    }
    return method;
  }

  // =====================================
  // OTP METHODS (Phone-based)
  // =====================================

  async sendOTP(phoneNumber: string): Promise<Result<OTPResponse>> {
    try {
      console.log("🔄 [AuthApiService] Sending OTP request:", {
        url: `${this.baseURL}/clinic/doctor/auth/send-otp`,
        phoneNumber,
      });

      const response = await fetch(
        `${this.baseURL}/clinic/doctor/auth/send-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phone_number: phoneNumber,
          }),
        }
      );

      console.log(
        "📡 [AuthApiService] OTP Response status:",
        response.status,
        response.statusText
      );

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ [AuthApiService] OTP Error response:", error);
        return Result.failure(error.detail || "Failed to send OTP");
      }

      const result = await response.json();
      console.log("✅ [AuthApiService] OTP Success response:", result);
      return Result.success(result.data);
    } catch (error) {
      console.log("💥 [AuthApiService] OTP Exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to send OTP"
      );
    }
  }

  async confirmOTP(
    phoneNumber: string,
    otpCode: string
  ): Promise<Result<ConfirmOTPResponse>> {
    const cacheKey = this.getCacheKey("confirmOTP", { phoneNumber, otpCode });

    return this.deduplicate(cacheKey, async () => {
      try {
        console.log("🔄 [AuthApiService] Confirming OTP:", {
          url: `${this.baseURL}/clinic/doctor/auth/verify-otp`,
          phoneNumber,
          otpCode,
        });

        const response = await fetch(
          `${this.baseURL}/clinic/doctor/auth/verify-otp`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              phone_number: phoneNumber,
              otp_code: otpCode,
            }),
          }
        );

        console.log(
          "📡 [AuthApiService] OTP Verify Response status:",
          response.status,
          response.statusText
        );

        if (!response.ok) {
          const error = await response.json();
          console.log("❌ [AuthApiService] OTP Verify Error response:", error);
          return Result.failure(error.detail || "Failed to verify OTP");
        }

        const result = await response.json();
        console.log("✅ [AuthApiService] OTP Verify Success response:", result);

        // Store tokens for authenticated requests
        if (result.data.access_token) {
          this.setTokens(
            result.data.access_token,
            result.data.refresh_token || "",
            result.data.expires_in
          );
        }

        // Store doctor data in localStorage
        if (result.data.doctor) {
          localStorage.setItem(
            "doctor_data",
            JSON.stringify(result.data.doctor)
          );
          console.log("✅ [AuthApiService] Doctor data stored in localStorage");
        }

        // Initialize registration progress if not exists
        if (!localStorage.getItem("registration_progress")) {
          const initialStep =
            result.data.doctor?.state === "pending_verification"
              ? "scan_id"
              : "scan_id";
          localStorage.setItem("registration_progress", initialStep);
          console.log("✅ [AuthApiService] Initial registration progress set to:", initialStep);
        }

        // Notify the application that doctor info may have changed
        triggerDoctorInfoRefresh();

        return Result.success(result.data);
      } catch (error) {
        return Result.failure(
          error instanceof Error ? error.message : "Failed to verify OTP"
        );
      }
    });
  }

  // =====================================
  // LICENSE-BASED OTP METHODS
  // =====================================

  async getPhoneByLicense(
    licenseNumber: string
  ): Promise<Result<GetPhoneByLicenseResponse>> {
    try {
      console.log("🔄 [AuthApiService] Getting phone by license:", {
        url: `${this.baseURL}/clinic/doctor/auth/get-phone/${licenseNumber}`,
        licenseNumber,
      });

      const response = await fetch(
        `${this.baseURL}/clinic/doctor/auth/get-phone/${licenseNumber}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log(
        "📡 [AuthApiService] Get Phone Response status:",
        response.status,
        response.statusText
      );

      const result = await response.json();

      // Check for API error format: { status: false, error: {...} }
      if (result.status === false && result.error) {
        console.log("❌ [AuthApiService] Get Phone Error response:", result.error);

        // Dispatch global error event
        window.dispatchEvent(new CustomEvent('api-error', {
          detail: {
            status: false,
            error: result.error
          }
        }));

        return Result.failure(result.error.message || "Failed to get phone number");
      }

      console.log("✅ [AuthApiService] Get Phone Success response:", result);
      return Result.success(result.data);
    } catch (error) {
      console.log("💥 [AuthApiService] Get Phone Exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to get phone number"
      );
    }
  }

  async sendOtpByLicense(licenseNumber: string): Promise<Result<OTPResponse>> {
    try {
      console.log("🔄 [AuthApiService] Sending OTP by license:", {
        url: `${this.baseURL}/clinic/doctor/auth/send-otp/${licenseNumber}`,
        licenseNumber,
      });

      const response = await fetch(
        `${this.baseURL}/clinic/doctor/auth/send-otp/${licenseNumber}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log(
        "📡 [AuthApiService] Send OTP by License Response status:",
        response.status,
        response.statusText
      );

      if (!response.ok) {
        const error = await response.json();
        console.log("❌ [AuthApiService] Send OTP by License Error response:", error);
        const message =
          error?.error?.message || error?.detail || "Failed to send OTP";
        return Result.failure(message, errorDetailsOf(error, response.status));
      }

      const result = await response.json();
      console.log("✅ [AuthApiService] Send OTP by License Success response:", result);
      return Result.success(result.data);
    } catch (error) {
      console.log("💥 [AuthApiService] Send OTP by License Exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to send OTP"
      );
    }
  }

  async confirmOtpByLicense(
    licenseNumber: string,
    otpCode: string
  ): Promise<Result<DoctorVerificationResponse>> {
    const cacheKey = this.getCacheKey("confirmOtpByLicense", {
      licenseNumber,
      otpCode,
    });

    return this.deduplicate(cacheKey, async () => {
      try {
        console.log("🔄 [AuthApiService] Confirming OTP by license:", {
          url: `${this.baseURL}/clinic/doctor/auth/verify-otp`,
          licenseNumber,
          otpCode,
        });

        const response = await fetch(
          `${this.baseURL}/clinic/doctor/auth/verify-otp`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              otp_code: otpCode,
              license_number: licenseNumber,
            }),
          }
        );

        console.log(
          "📡 [AuthApiService] Confirm OTP by License Response status:",
          response.status,
          response.statusText
        );

        if (!response.ok) {
          const error = await response.json();
          console.log("❌ [AuthApiService] Confirm OTP by License Error response:", error);
          const message =
            error?.error?.message || error?.detail || "Failed to verify OTP";
          return Result.failure(message, error?.error?.details);
        }

        const result = await response.json();
        console.log("✅ [AuthApiService] Confirm OTP by License Success response:", result);

        // Store tokens if provided
        if (result.data.access_token) {
          this.setTokens(
            result.data.access_token,
            result.data.refresh_token || "",
            result.data.expires_in
          );
        }

        // Store doctor data in localStorage
        if (result.data.doctor) {
          localStorage.setItem(
            "doctor_data",
            JSON.stringify(result.data.doctor)
          );
          console.log("✅ [AuthApiService] Doctor data stored in localStorage");
        }

        // Initialize registration progress if not exists
        if (!localStorage.getItem("registration_progress")) {
          const initialStep =
            result.data.doctor?.state === "pending_verification"
              ? "scan_id"
              : "scan_id";
          localStorage.setItem("registration_progress", initialStep);
          console.log("✅ [AuthApiService] Initial registration progress set to:", initialStep);
        }

        // Notify the application that doctor info may have changed
        triggerDoctorInfoRefresh();

        return Result.success(result.data);
      } catch (error) {
        return Result.failure(
          error instanceof Error ? error.message : "Failed to verify OTP"
        );
      }
    });
  }

  // =====================================
  // IMA ID QR VERIFICATION (DMIS-unavailable fallback)
  // =====================================

  async verifyQr(uidOrUrl: string, enteredLicense?: number): Promise<Result<QrVerifyResponse>> {
    try {
      const response = await fetch(`${this.baseURL}/clinic/doctor/auth/verify-qr`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid_or_url: uidOrUrl, entered_license: enteredLicense }),
      });
      const result = await response.json();
      if (!response.ok || result.status === false) {
        const message = result?.error?.message || "Failed to verify QR";
        return Result.failure(message, result?.error?.details);
      }
      return Result.success(result.data as QrVerifyResponse);
    } catch (error) {
      return Result.failure(error instanceof Error ? error.message : "Failed to verify QR");
    }
  }

  async qrManualSubmit(payload: QrManualSubmitPayload): Promise<Result<QrManualSubmitResponse>> {
    try {
      const response = await fetch(`${this.baseURL}/clinic/doctor/auth/qr/manual-submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (!response.ok || result.status === false) {
        const message = result?.error?.message || "Failed to submit identity";
        return Result.failure(message, result?.error?.details);
      }
      return Result.success(result.data as QrManualSubmitResponse);
    } catch (error) {
      return Result.failure(error instanceof Error ? error.message : "Failed to submit identity");
    }
  }

  async qrSendOtp(
    qrPendingToken: string,
    countryCode: string,
    phoneNumber: string
  ): Promise<Result<OTPResponse>> {
    try {
      const response = await fetch(`${this.baseURL}/clinic/doctor/auth/qr/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          qr_pending_token: qrPendingToken,
          country_code: countryCode,
          phone_number: phoneNumber,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status === false) {
        const message = result?.error?.message || "Failed to send OTP";
        return Result.failure(message, errorDetailsOf(result, response.status));
      }
      return Result.success(result.data);
    } catch (error) {
      return Result.failure(error instanceof Error ? error.message : "Failed to send OTP");
    }
  }

  async qrVerifyOtp(
    qrPendingToken: string,
    countryCode: string,
    phoneNumber: string,
    otpCode: string
  ): Promise<Result<DoctorVerificationResponse>> {
    try {
      const response = await fetch(`${this.baseURL}/clinic/doctor/auth/qr/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          qr_pending_token: qrPendingToken,
          country_code: countryCode,
          phone_number: phoneNumber,
          otp_code: otpCode,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status === false) {
        const message = result?.error?.message || "Failed to verify OTP";
        return Result.failure(message, errorDetailsOf(result, response.status));
      }

      // Store tokens + doctor data + init registration progress (mirrors confirmOtpByLicense).
      if (result.data.access_token) {
        this.setTokens(
          result.data.access_token,
          result.data.refresh_token || "",
          result.data.expires_in
        );
      }
      if (result.data.doctor) {
        localStorage.setItem("doctor_data", JSON.stringify(result.data.doctor));
      }
      if (!localStorage.getItem("registration_progress")) {
        localStorage.setItem("registration_progress", "scan_id");
      }
      triggerDoctorInfoRefresh();

      return Result.success(result.data);
    } catch (error) {
      return Result.failure(error instanceof Error ? error.message : "Failed to verify OTP");
    }
  }

  // =====================================
  // TOKEN REFRESH
  // =====================================

  /**
   * Refresh with rotation: every successful refresh returns a NEW refresh token and the old one
   * stops working, so the new pair must be stored before anything else uses the old one.
   * - One refresh at a time per tab (callers share the in-flight promise) and, where the browser
   *   supports the Web Locks API, one at a time across tabs.
   * - If another tab rotated the tokens while we waited, we adopt its pair instead of sending the
   *   now-revoked refresh token again.
   * - Tokens are cleared only when the server rejects the refresh token we actually sent.
   * @param rejectedAccessToken the access token the server just rejected (if known). When the
   *   stored token is already a different one, the caller simply retries with it.
   */
  async refreshAccessToken(rejectedAccessToken?: string | null): Promise<Result<RefreshTokenResponse>> {
    if (this.refreshInFlight) return this.refreshInFlight;
    const rejected = rejectedAccessToken === undefined ? this.accessToken : rejectedAccessToken;
    const run = () => this.performRefresh(rejected);
    const locks: any = typeof navigator !== "undefined" ? (navigator as any).locks : undefined;
    const promise: Promise<Result<RefreshTokenResponse>> =
      locks && typeof locks.request === "function"
        ? Promise.resolve(locks.request(REFRESH_LOCK_NAME, run)).catch(() => run())
        : run();
    this.refreshInFlight = promise.finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private currentPair(): RefreshTokenResponse {
    const expiry = this.getTokenExpiry();
    return {
      access_token: this.accessToken || "",
      refresh_token: this.refreshToken || "",
      expires_in: expiry ? Math.max(0, Math.round((expiry - Date.now()) / 1000)) : 0,
      token_type: "Bearer",
    };
  }

  private async performRefresh(rejectedAccessToken: string | null): Promise<Result<RefreshTokenResponse>> {
    // Another tab (or an earlier refresh) may already have replaced the pair.
    this.loadStoredTokens();
    if (this.accessToken && this.accessToken !== rejectedAccessToken && this.isTokenValid()) {
      return Result.success(this.currentPair());
    }

    const sentRefreshToken = this.refreshToken;
    if (!sentRefreshToken) {
      return Result.failure("No refresh token available");
    }

    try {
      const response = await fetch(`${this.baseURL}/clinic/doctor/auth/refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
        },
        body: JSON.stringify({ refresh_token: sentRefreshToken }),
      });

      let body: any = null;
      try {
        body = await response.json();
      } catch {
        body = null;
      }

      if (!response.ok || !body || body.status === false || !body.data?.access_token) {
        // Lost a race with another tab that rotated the same refresh token: use its pair.
        const stored = localStorage.getItem("refresh_token");
        if (stored && stored !== sentRefreshToken) {
          this.loadStoredTokens();
          return Result.success(this.currentPair());
        }
        // The refresh token we sent is expired, revoked, already used or not a refresh token.
        if (REFRESH_REJECTED_STATUSES.includes(response.status)) {
          this.clearTokens();
          triggerDoctorSessionExpired();
        }
        const message =
          body?.error?.message || body?.detail || body?.message || "Failed to refresh token";
        return Result.failure(typeof message === "string" ? message : "Failed to refresh token");
      }

      const data = body.data;
      // Rotation: always store the refresh token that came back. Older servers that do not rotate
      // return none, and the current one stays valid.
      this.setTokens(data.access_token, data.refresh_token || sentRefreshToken, data.expires_in);
      return Result.success({
        access_token: data.access_token,
        refresh_token: data.refresh_token || sentRefreshToken,
        expires_in: data.expires_in,
        token_type: data.token_type || "Bearer",
      });
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to refresh token"
      );
    }
  }

  // =====================================
  // LOGOUT
  // =====================================

  async logout(): Promise<Result<{ message: string }>> {
    this.loadStoredTokens();

    if (!this.refreshToken) {
      this.clearTokens();
      return Result.success({ message: "Logged out successfully" });
    }

    try {
      // The server revokes the refresh token from the body and the access token from the header.
      const response = await fetch(
        `${this.baseURL}/clinic/doctor/auth/logout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            accept: "application/json",
            ...(this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
          },
          body: JSON.stringify({
            refresh_token: this.refreshToken,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        this.clearTokens();

        return Result.failure(
          error.error_message || error.detail || "Failed to logout"
        );
      }

      const result = await response.json();

      if (result.status) {
        this.clearTokens();
        return Result.success(result.data);
      } else {
        this.clearTokens();
        return Result.failure(result.message || "Logout failed");
      }
    } catch (error) {
      this.clearTokens();

      if (error instanceof TypeError && error.message.includes("fetch")) {
        return Result.success({ message: "Logged out successfully (offline)" });
      }

      return Result.failure(
        error instanceof Error ? error.message : "Failed to logout"
      );
    }
  }

  // =====================================
  // DOCTOR PROFILE
  // =====================================

  async getMyInfo(): Promise<Result<any>> {
    if (isMockEnabled()) {
      return Result.success(getMockMyInfo());
    }

    console.log("🎯 [AuthApiService] Starting getMyInfo call...");
    this.loadStoredTokens();

    if (!this.accessToken) {
      console.log("❌ [AuthApiService] Profile request failed: No authentication token available");
      return Result.failure("No authentication token available");
    }

    try {
      console.log("🔄 [AuthApiService] Fetching doctor profile from /clinic/doctor/profile/me");
      const sentToken = this.accessToken;

      const response = await fetch(`${this.baseURL}/clinic/doctor/profile/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.accessToken}`,
          "Content-Type": "application/json",
        },
      });

      console.log(
        "📡 [AuthApiService] Profile Response status:",
        response.status,
        response.statusText
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.log("❌ [AuthApiService] Profile Error response:", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });

        if (response.status === 401 || response.status === 444) {
          const refreshResult = await this.refreshAccessToken(sentToken);
          if (refreshResult.isSuccess) {
            return this.getMyInfo();
          }
        }

        let error;
        try {
          error = JSON.parse(errorText);
        } catch {
          error = { detail: errorText || `HTTP ${response.status}: ${response.statusText}` };
        }
        return Result.failure(error.detail || "Failed to get doctor info");
      }

      const result = await response.json();
      console.log("✅ [AuthApiService] Profile Success response:", result);

      // Transform the response to match the expected structure
      // The API returns doctor data directly in result.data
      // But the application expects { doctor_info: ..., clinic_info: ..., etc. }
      const transformedData = {
        doctor_info: result.data,
        clinic_info: result.data?.active_clinic || null,
        doctor_docs: result.data?.doctor_docs || [],
        clinic_docs: result.data?.clinic_docs || [],
        subscription_info: result.data?.subscription_info || null,
        is_junior: result.data?.is_junior || false,
      };

      console.log("✅ [AuthApiService] Transformed profile data:", transformedData);
      return Result.success(transformedData);
    } catch (error) {
      console.log("💥 [AuthApiService] Profile Exception:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to get doctor info"
      );
    }
  }

  // =====================================
  // SHARED AUTHENTICATED FETCH
  // =====================================

  async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    if (!this.accessToken) {
      this.loadStoredTokens();
    }

    if (!this.accessToken) {
      throw new Error("No authentication token available");
    }

    const isFormData = options.body instanceof FormData;
    const buildHeaders = () => ({
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(options.headers as Record<string, string>),
      Authorization: `Bearer ${this.accessToken}`,
    });

    const sentToken = this.accessToken;
    const response = await fetch(url, {
      ...options,
      headers: buildHeaders(),
    });

    if (response.status === 401 || response.status === 444) {
      const refreshResult = await this.refreshAccessToken(sentToken);
      if (refreshResult.isSuccess) {
        return fetch(url, {
          ...options,
          headers: buildHeaders(),
        });
      }
    }

    return response;
  }
}
